import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, Send, Copy, Home } from "lucide-react";
import { useApp } from "../context/AppContext";
import PartnerBadge from "../components/PartnerBadge";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const SuccessScreen = () => {
  const nav = useNavigate();
  const { activeTx, partner, resetDraft, setActiveTx } = useApp();
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!activeTx) nav("/demo", { replace: true });
  }, [activeTx, nav]);

  if (!activeTx) return null;

  const copyRef = () => {
    navigator.clipboard?.writeText(activeTx.reference);
    setCopied(true);
    toast.success("Reference copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const handleSendReceipt = () => {
    setSent(true);
    toast.success("Receipt sent successfully");
  };

  const done = () => {
    resetDraft();
    setActiveTx(null);
    nav("/demo", { replace: true });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="h-full flex flex-col px-6 pt-8 pb-6"
      data-testid="success-screen"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 240, damping: 18, delay: 0.1 }}
        className="mx-auto w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center"
      >
        <Check className="w-10 h-10 text-white" strokeWidth={3} />
      </motion.div>

      <h1 className="font-display text-[26px] font-bold text-center mt-6 tracking-tight leading-tight dark:text-white">
        Payment completed
        <br />
        successfully
      </h1>
      <div className="text-center text-[13px] text-gray-500 dark:text-gray-400 mt-2">
        {activeTx.amount.toFixed(2)} kr to <b className="text-gray-900 dark:text-gray-200">{activeTx.recipient.name}</b>
      </div>

      <div className="mt-5 mx-auto">
        <PartnerBadge
          partner={partner.name}
          variant="pill"
          prefix="Executed by"
          testId="success-partner-badge"
        />
      </div>

      {/* Receipt card */}
      <div className="mt-6 rounded-3xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5 space-y-3 text-[13px]">
        <Row label="Recipient gets" value={`${activeTx.recipientGets.toFixed(2)} kr`} />
        <Row label="Fees" value={`${activeTx.fee.toFixed(2)} kr`} />
        <Row label="Payment method" value={activeTx.method} />
        <Row label="Executed by" value={activeTx.partner} highlight />
        <Row
          label="Reference"
          testIdValue="success-reference"
          value={
            <button
              onClick={copyRef}
              data-testid="copy-reference-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[12px] text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
            >
              {activeTx.reference}
              <Copy className="w-3 h-3" strokeWidth={2.2} />
            </button>
          }
        />
        <Row
          label="Date"
          value={new Date(activeTx.createdAt).toLocaleString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        />
      </div>

      {copied && (
        <div className="text-center text-[11px] text-emerald-600 font-semibold mt-2">
          Reference copied
        </div>
      )}

      <div className="mt-auto space-y-2 pt-6">
        <button
          data-testid="send-receipt-btn"
          onClick={handleSendReceipt}
          disabled={sent}
          className="w-full bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white h-12 rounded-full font-semibold text-[14px] hover:bg-gray-200 dark:hover:bg-white/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Send className="w-4 h-4" strokeWidth={2.2} />
          {sent ? "Receipt sent" : "Send receipt"}
        </button>
        <button
          data-testid="success-done-btn"
          onClick={done}
          className="w-full bg-gray-900 dark:bg-emerald-600 text-white h-12 rounded-full font-semibold text-[14px] hover:bg-black dark:hover:bg-emerald-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" strokeWidth={2.2} />
          Done
        </button>
      </div>
    </motion.div>
  );
};

const Row = ({ label, value, highlight, testIdValue }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span
      data-testid={testIdValue}
      className={
        "font-semibold " + (highlight ? "text-gray-900 dark:text-white" : "text-gray-900 dark:text-white")
      }
    >
      {value}
    </span>
  </div>
);

export default SuccessScreen;
